import React, { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useMutation } from "@apollo/client";
import {
    CREATE_GIST_WITH_CONTENT,
    GET_ALL_GISTS,
    GET_ALL_SUBJECTS,
    GET_GISTS_BY_SUBJECT,
} from "../../services/graphql/queriesMutations";
import RichEditor from "../../components/dashboard/RichEditor";

type FormValues = {
    title: string;
    point: string;
};

interface GistEditorFormProps {
    subjectId: number;
    parentGistId?: number; // Optional for replies
    onSuccess: () => void;
    onCancel: () => void;
    publishedSubmitLabel?: string;
    draftSubmitLabel?: string;
    savingLabel?: string;
    draftOptionLabel?: string;
    defaultSaveAsDraft?: boolean;
}

/**
 * Shared form component for creating Gists.
 * Used in both Tree view (CreateGistModal) and Reply functionality.
 *
 * Uses createGistWithContent — a single mutation that creates Gist + Version +
 * Edit atomically inside one Prisma database transaction on the server.
 * Partial creation is impossible: if any DB write fails the transaction rolls
 * back and nothing is persisted.
 */
const GistEditorForm: React.FC<GistEditorFormProps> = ({
    subjectId,
    parentGistId,
    onSuccess,
    onCancel,
    publishedSubmitLabel = "Publish",
    draftSubmitLabel = "Save Draft",
    savingLabel = "Saving...",
    draftOptionLabel = "Save as private draft instead of publishing now",
    defaultSaveAsDraft = false,
}) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormValues>();

    const [content, setContent] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);
    const [contentError, setContentError] = useState(false);
    const [saveAsDraft, setSaveAsDraft] = useState(defaultSaveAsDraft);

    // Single atomic mutation — replaces the old 3-step addGist/addVersion/addEdit flow.
    // refetchQueries keeps both the subject sidebar and the gist list in sync (A1 fix).
    const [createGistWithContent] = useMutation(CREATE_GIST_WITH_CONTENT, {
        refetchQueries: [
            { query: GET_ALL_SUBJECTS },
            { query: GET_ALL_GISTS },
            { query: GET_GISTS_BY_SUBJECT, variables: { subjectId } },
        ],
        awaitRefetchQueries: true,
    });

    const onSubmit: SubmitHandler<FormValues> = async (formData) => {
        if (content.length < 10) {
            setContentError(true);
            return;
        }
        setContentError(false);
        setIsLoading(true);

        try {
            await createGistWithContent({
                variables: {
                    input: {
                        title:     formData.title,
                        subjectId,
                        point:     formData.point,
                        body:      content,
                        status:    saveAsDraft ? "DRAFT" : "PUBLISHED",
                        ...(parentGistId && { parentId: parentGistId }),
                    },
                },
            });

            // Only called when the server transaction commits successfully.
            onSuccess();

        } catch (e: any) {
            console.error("❌ Error creating Gist:", e);
            alert(e.message || "Failed to create Gist. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} style={{ fontFamily: 'var(--font-primary)' }}>
            {/* Title Field */}
            <div style={{ marginBottom: 16 }}>
                <label style={{
                    display: 'block',
                    marginBottom: 6,
                    color: 'var(--color-charcoal)',
                    fontWeight: 500,
                    fontSize: 14,
                }}>
                    Title
                </label>
                <input
                    type="text"
                    {...register("title", { required: "Title is required" })}
                    placeholder="Enter gist title"
                    style={{
                        width: '100%',
                        padding: '10px 12px',
                        fontSize: 14,
                        border: errors.title ? '1px solid var(--color-terracotta)' : '1px solid var(--color-border)',
                        borderRadius: 6,
                        outline: 'none',
                    }}
                />
                {errors.title && (
                    <span style={{ color: 'var(--color-terracotta)', fontSize: 12 }}>
                        {errors.title.message}
                    </span>
                )}
            </div>

            {/* Point Field */}
            <div style={{ marginBottom: 16 }}>
                <label style={{
                    display: 'block',
                    marginBottom: 6,
                    color: 'var(--color-charcoal)',
                    fontWeight: 500,
                    fontSize: 14,
                }}>
                    Point (Summary)
                </label>
                <textarea
                    {...register("point", { required: "Point is required" })}
                    placeholder="Enter a brief summary"
                    rows={2}
                    style={{
                        width: '100%',
                        padding: '10px 12px',
                        fontSize: 14,
                        border: errors.point ? '1px solid var(--color-terracotta)' : '1px solid var(--color-border)',
                        borderRadius: 6,
                        outline: 'none',
                        resize: 'vertical',
                    }}
                />
                {errors.point && (
                    <span style={{ color: 'var(--color-terracotta)', fontSize: 12 }}>
                        {errors.point.message}
                    </span>
                )}
            </div>

            {/* Body/Content Field */}
            <div style={{ marginBottom: 16 }}>
                <label style={{
                    display: 'block',
                    marginBottom: 6,
                    color: 'var(--color-charcoal)',
                    fontWeight: 500,
                    fontSize: 14,
                }}>
                    Body Content
                </label>
                <div style={{
                    border: contentError ? '1px solid var(--color-terracotta)' : '1px solid var(--color-border)',
                    borderRadius: 6,
                    overflow: 'hidden',
                    maxHeight: 200,
                    overflowY: 'auto',
                }}>
                    <RichEditor
                        editable={true}
                        content={content}
                        setContent={setContent}
                    />
                </div>
                {contentError && (
                    <span style={{ color: 'var(--color-terracotta)', fontSize: 12 }}>
                        Please provide at least 10 characters of content
                    </span>
                )}
            </div>

            <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 16,
                color: 'var(--color-charcoal)',
                fontSize: 13,
            }}>
                <input
                    type="checkbox"
                    checked={saveAsDraft}
                    onChange={(event) => setSaveAsDraft(event.target.checked)}
                />
                {draftOptionLabel}
            </label>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={isLoading}
                    style={{
                        padding: '10px 20px',
                        borderRadius: 8,
                        border: '1px solid var(--color-border)',
                        backgroundColor: 'white',
                        color: 'var(--color-charcoal)',
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 500,
                    }}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={isLoading}
                    style={{
                        padding: '10px 24px',
                        borderRadius: 8,
                        border: 'none',
                        backgroundColor: 'var(--color-terracotta)',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 600,
                        opacity: isLoading ? 0.6 : 1,
                    }}
                >
                    {isLoading ? savingLabel : saveAsDraft ? draftSubmitLabel : publishedSubmitLabel}
                </button>
            </div>
        </form>
    );
};

export default GistEditorForm;
